import { Slider, Upload } from 'antd'
import t from 'prop-types'
import { forwardRef, useCallback, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import Cropper from 'react-easy-crop'
import { useTranslation } from 'react-i18next'
import { FaUpload } from 'react-icons/fa'
import { compressImage } from 'services/imageProcess'
import { ImageCropperContainer } from './style'

const { Dragger } = Upload

const ZOOM_STEP = 0.1
const MIN_ROTATE = 0
const MAX_ROTATE = 360
const ROTATE_STEP = 1

const EasyCrop = forwardRef(
  (
    {
      processedFile,
      aspect,
      shape,
      grid,

      hasZoom,
      zoomVal,
      rotateVal,
      setZoomVal,
      setRotateVal,

      minZoom,
      maxZoom,
      onCrop,

      cropperProps,
    },
    ref,
  ) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [cropSize, setCropSize] = useState({ width: 0, height: 0 })

    const onMediaLoaded = useCallback(
      (mediaSize) => {
        const { width, height } = mediaSize
        const ratioWidth = height * aspect

        if (width > ratioWidth) {
          setCropSize({ width: ratioWidth, height })
        } else {
          setCropSize({ width, height: width / aspect })
        }
      },
      [aspect],
    )

    return (
      <Cropper
        {...cropperProps}
        ref={ref}
        image={processedFile}
        crop={crop}
        cropSize={cropSize}
        onCropChange={setCrop}
        aspect={aspect}
        cropShape={shape}
        showGrid={grid}
        zoomWithScroll={hasZoom}
        zoom={zoomVal}
        rotation={rotateVal}
        onZoomChange={setZoomVal}
        onRotationChange={setRotateVal}
        minZoom={minZoom}
        maxZoom={maxZoom}
        onCropComplete={(_, vec) => onCrop(vec)}
        onMediaLoaded={onMediaLoaded}
        classes={{
          containerClassName: 'cropper-container',
          mediaClassName: 'cropper-media',
        }}
      />
    )
  },
)

EasyCrop.displayName = 'EasyCrop'

EasyCrop.propTypes = {
  processedFile: t.string,
  aspect: t.number,
  shape: t.string,
  grid: t.bool,

  hasZoom: t.bool,
  zoomVal: t.number,
  rotateVal: t.number,
  setZoomVal: t.func,
  setRotateVal: t.func,

  minZoom: t.number,
  maxZoom: t.number,
  onCrop: t.func,

  cropperProps: t.object,
}

const ImageCropper = forwardRef((props, ref) => {
  const {
    shape,
    grid,
    quality,

    zoom,
    rotate,
    minZoom,
    maxZoom,
    fillColor,

    modalWidth,
    modalOk,
    modalCancel,

    cropperProps,

    onRemove,
    onAddPendingUpload,
    pendingUploads,
  } = props
  const { t } = useTranslation()
  const hasZoom = zoom === true
  const hasRotate = rotate === true

  const [zoomVal, setZoomVal] = useState(1)
  const [rotateVal, setRotateVal] = useState(0)
  const [isHorizontal, setIsHorizontal] = useState(true)
  const [file, setFile] = useState(null)
  const [processedFile, setProcessedFile] = useState(null)
  const [cropVector, setCropVector] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  })

  const rateH = 1.5714285714285714
  const rateV = 0.6363636363636364

  /**
   * Controls
   */
  const isMinZoom = zoomVal - ZOOM_STEP < minZoom
  const isMaxZoom = zoomVal + ZOOM_STEP > maxZoom
  const isMinRotate = rotateVal === MIN_ROTATE
  const isMaxRotate = rotateVal === MAX_ROTATE

  const subZoomVal = useCallback(() => {
    if (!isMinZoom) setZoomVal(zoomVal - ZOOM_STEP)
  }, [isMinZoom, zoomVal])

  const addZoomVal = useCallback(() => {
    if (!isMaxZoom) setZoomVal(zoomVal + ZOOM_STEP)
  }, [isMaxZoom, zoomVal])

  const subRotateVal = useCallback(() => {
    if (!isMinRotate) setRotateVal(rotateVal - ROTATE_STEP)
  }, [isMinRotate, rotateVal])

  const addRotateVal = useCallback(() => {
    if (!isMaxRotate) setRotateVal(rotateVal + ROTATE_STEP)
  }, [isMaxRotate, rotateVal])

  /**
   * Modal
   */
  const modalProps = useMemo(() => {
    const obj = { width: modalWidth, okText: modalOk, cancelText: modalCancel }
    Object.keys(obj).forEach((key) => {
      if (!obj[key]) delete obj[key]
    })
    return obj
  }, [modalCancel, modalOk, modalWidth])

  const onClose = useCallback(() => {
    setProcessedFile(null)
    setZoomVal(1)
    setRotateVal(0)
  }, [])

  const onOk = useCallback(async () => {
    onClose()

    const naturalImg = document.querySelector('.cropper-media')
    const { naturalWidth, naturalHeight } = naturalImg
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // create a max canvas to cover the source image after rotated
    const maxLen = Math.sqrt(
      Math.pow(naturalWidth, 2) + Math.pow(naturalHeight, 2),
    )
    canvas.width = maxLen
    canvas.height = maxLen

    // rotate the image
    if (hasRotate && rotateVal > 0 && rotateVal < 360) {
      const halfMax = maxLen / 2
      ctx.translate(halfMax, halfMax)
      ctx.rotate((rotateVal * Math.PI) / 180)
      ctx.translate(-halfMax, -halfMax)
    }

    ctx.fillStyle = fillColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // draw the source image in the center of the max canvas
    const left = (maxLen - naturalWidth) / 2
    const top = (maxLen - naturalHeight) / 2
    ctx.drawImage(naturalImg, left, top)

    // shrink the max canvas to the crop area size, then align two center points
    const maxImgData = ctx.getImageData(0, 0, maxLen, maxLen)
    const { width, height, x, y } = cropVector
    canvas.width = width
    canvas.height = height
    ctx.putImageData(maxImgData, Math.round(-left - x), Math.round(-top - y))

    // get the new image
    const { type, name, uid } = file
    canvas.toBlob(
      async (blob) => {
        let newFile = new File([blob], name, { type })
        newFile.uid = uid
        const compressedFile = await compressImage(
          newFile,
          canvas.width > canvas.height,
        )
        onAddPendingUpload(compressedFile)
      },
      type,
      quality,
    )
  }, [
    fillColor,
    hasRotate,
    onClose,
    quality,
    rotateVal,
    cropVector,
    onAddPendingUpload,
    file,
  ])

  return (
    <div>
      <Dragger
        onRemove={onRemove}
        beforeUpload={(file) => {
          setFile(file)
          const reader = new FileReader()
          reader.addEventListener('load', () => {
            setProcessedFile(reader.result)
          })
          reader.readAsDataURL(file)
        }}
        fileList={pendingUploads}
        showUploadList={false}
        accept="image/*"
        onChange={() => {}}
        onDrop={() => {}}
      >
        <p className="ant-upload-drag-icon hor">
          <FaUpload />
        </p>
        <p className="ant-upload-text">{t('drag_and_drop_pictures_here')}</p>
        <Button className="btn-blue btn-dash choose-picture">
          {t('choose_pictures')}
        </Button>
      </Dragger>
      {processedFile && (
        <ImageCropperContainer
          visible={true}
          wrapClassName="cropper-modal"
          getContainer={() => document.getElementById('story-section')}
          title="Edit Image"
          onOk={onOk}
          onCancel={onClose}
          maskClosable={false}
          destroyOnClose
          {...modalProps}
        >
          <div className="modal-custom-body p20">
            {isHorizontal ? (
              <EasyCrop
                ref={ref}
                processedFile={processedFile}
                aspect={rateH}
                shape={shape}
                grid={grid}
                hasZoom={hasZoom}
                zoomVal={zoomVal}
                rotateVal={rotateVal}
                setZoomVal={setZoomVal}
                setRotateVal={setRotateVal}
                minZoom={minZoom}
                maxZoom={maxZoom}
                onCrop={(vec) => setCropVector(vec)}
                cropperProps={cropperProps}
              />
            ) : (
              /* 
                Hack to redo the ref when switching from horizontal to vertical 
                TODO: Fix properly
                */
              <span>
                <EasyCrop
                  ref={ref}
                  processedFile={processedFile}
                  rouch
                  aspect={rateV}
                  shape={shape}
                  grid={grid}
                  hasZoom={hasZoom}
                  zoomVal={zoomVal}
                  rotateVal={rotateVal}
                  setZoomVal={setZoomVal}
                  setRotateVal={setRotateVal}
                  minZoom={minZoom}
                  maxZoom={maxZoom}
                  onCrop={(vec) => setCropVector(vec)}
                  cropperProps={cropperProps}
                />
              </span>
            )}
          </div>
          <div className="modal-custom-body p20 mt-20">
            <div className="hor-ver-control">
              <input
                type="radio"
                className="mr-2"
                value="Horizontal"
                onChange={() => setIsHorizontal(true)}
                checked={isHorizontal}
              />{' '}
              {t('horizontal')}
              <div style={{ width: '40%' }}></div>
              <input
                type="radio"
                className="mr-2"
                value="Vertical"
                onChange={() => setIsHorizontal(false)}
                checked={!isHorizontal}
              />{' '}
              {t('vertical')}
            </div>
            {hasZoom && (
              <div className="cropper-control zoom">
                <button onClick={subZoomVal} disabled={isMinZoom}>
                  －
                </button>
                <Slider
                  min={minZoom}
                  max={maxZoom}
                  step={ZOOM_STEP}
                  value={zoomVal}
                  onChange={setZoomVal}
                />
                <button onClick={addZoomVal} disabled={isMaxZoom}>
                  ＋
                </button>
              </div>
            )}
            {hasRotate && (
              <div className="cropper-control rotate">
                <button onClick={subRotateVal} disabled={isMinRotate}>
                  ↺
                </button>
                <Slider
                  min={MIN_ROTATE}
                  max={MAX_ROTATE}
                  step={ROTATE_STEP}
                  value={rotateVal}
                  onChange={setRotateVal}
                />
                <button onClick={addRotateVal} disabled={isMaxRotate}>
                  ↻
                </button>
              </div>
            )}
          </div>
        </ImageCropperContainer>
      )}
    </div>
  )
})

ImageCropper.displayName = 'ImageCropper'

ImageCropper.propTypes = {
  aspect: t.number,
  shape: t.oneOf(['rect', 'round']),
  grid: t.bool,
  quality: t.number,

  zoom: t.bool,
  rotate: t.bool,
  minZoom: t.number,
  maxZoom: t.number,
  fillColor: t.string,

  modalTitle: t.string,
  modalWidth: t.oneOfType([t.number, t.string]),
  modalOk: t.string,
  modalCancel: t.string,

  cropperProps: t.object,
}

ImageCropper.defaultProps = {
  aspect: 1,
  shape: 'rect',
  grid: false,
  quality: 1,

  zoom: true,
  rotate: false,
  minZoom: 1,
  maxZoom: 3,
  fillColor: 'white',
}

export default ImageCropper
